import Phaser from 'phaser';
import { RunManager } from '../systems/RunManager';
import { CampManager } from '../systems/CampManager';
import { MetaManager } from '../systems/MetaManager';
import { ExpeditionManager, ExpeditionResult } from '../systems/ExpeditionManager';
import { EXPEDITION_DEFINITIONS } from '../data/expeditions';
import { ITEM_DEFINITIONS } from '../data/items';
import { RESOURCE_INFO, ResourceType } from '../systems/ResourceManager';
import { SfxManager } from '../utils/SfxManager';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';

// === SCÈNE DE RÉSULTAT D'EXPÉDITION ===
// Affichée au retour de run si une expédition idle était en cours.
// Montre les récompenses et les pertes éventuelles.

const HEADER_H = 70;

export class ExpeditionResultScene extends Phaser.Scene {
  private panel!: ScrollablePanel;
  private run!: RunManager;
  private camp!: CampManager;
  private meta!: MetaManager;
  private result!: ExpeditionResult;
  private sfx = new SfxManager();
  private victory = false;

  constructor() {
    super({ key: 'ExpeditionResultScene' });
  }

  init(data: { run: RunManager; result: ExpeditionResult; victory: boolean }): void {
    this.run = data.run;
    this.camp = this.run.getCamp();
    this.meta = this.run.getMeta();
    this.result = data.result;
    this.victory = data.victory;

    // Appliquer les récompenses
    this.applyRewards();
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e).setDepth(0);

    // === HEADER FIXE ===
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x0a0a1e).setDepth(10);

    const expDef = EXPEDITION_DEFINITIONS[this.result.expeditionId];
    this.add.text(GAME_CX, 15, `${expDef.emoji} RAPPORT D'EXPÉDITION`, {
      fontSize: '18px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    this.add.text(GAME_CX, 40, expDef.name, {
      fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(11);

    // Bouton continuer (ancré à droite — responsive)
    const continueBtn = this.add.text(GAME_W - 8, 12, '→ Continuer', {
      fontSize: '11px', color: '#f1c40f', fontFamily: FONT_BODY,
      backgroundColor: '#2c3e50', padding: { x: 8, y: 4 },
    }).setOrigin(1, 0).setDepth(11);
    continueBtn.setInteractive({ useHandCursor: true });
    continueBtn.on('pointerdown', () => {
      startSceneWithFade(this, 'RunEndScene', { run: this.run, victory: this.victory });
    });
    continueBtn.on('pointerover', () => continueBtn.setColor('#ffffff'));
    continueBtn.on('pointerout', () => continueBtn.setColor('#f1c40f'));

    // === CONTENU SCROLLABLE ===
    this.panel = new ScrollablePanel(this, 0, HEADER_H, GAME_W, GAME_H - HEADER_H);
    let y = 15;

    // Positions responsive : sur mobile (450 px), on rapproche les marges.
    const _mobERSHead = GAME_W < 500;
    const headerX = _mobERSHead ? 15 : 130;
    const itemX = _mobERSHead ? 25 : 150;

    // === RÉCOMPENSES ===
    this.panel.add(this.add.text(headerX, y, '--- Récompenses ---', {
      fontSize: '11px', color: '#2ecc71', fontFamily: FONT_BODY,
    }));
    y += 22;

    // Ressources
    const resEntries = Object.entries(this.result.resources) as [ResourceType, number][];
    if (resEntries.length > 0) {
      const resLabel = resEntries
        .filter(([, amt]) => amt > 0)
        .map(([type, amt]) => `${RESOURCE_INFO[type].emoji} +${amt} ${RESOURCE_INFO[type].name}`)
        .join('   ');
      this.panel.add(this.add.text(itemX, y, resLabel, {
        fontSize: '11px', color: '#bdc3c7', fontFamily: FONT_BODY,
        wordWrap: { width: GAME_W - itemX - 10 },
      }));
      y += 22;
    }

    // Or
    if (this.result.gold > 0) {
      this.panel.add(this.add.text(itemX, y, `💰 +${this.result.gold} or`, {
        fontSize: '11px', color: '#f1c40f', fontFamily: FONT_BODY,
      }));
      y += 22;
    }

    // Item trouvé
    if (this.result.itemFound) {
      const itemDef = ITEM_DEFINITIONS[this.result.itemFound];
      if (itemDef) {
        this.panel.add(this.add.text(itemX, y, `🎁 ${itemDef.emoji} ${itemDef.name} trouvé ! (ajouté au coffre)`, {
          fontSize: '11px', color: '#f1c40f', fontFamily: FONT_BODY,
          wordWrap: { width: GAME_W - itemX - 10 },
        }));
        y += 22;
      }
    }

    // === ÉQUIPE ===
    y += 10;
    this.panel.add(this.add.text(headerX, y, `--- Équipe (${this.result.deaths} mort(s)) ---`, {
      fontSize: '11px', color: this.result.deaths > 0 ? '#e74c3c' : '#2ecc71', fontFamily: FONT_BODY,
    }));
    y += 22;

    const _mobERS = GAME_W < 500;
    for (const sr of this.result.survivors) {
      const statusEmoji = sr.alive ? '✅' : '💀';
      const statusColor = sr.alive ? '#2ecc71' : '#e74c3c';
      const statusLabel = sr.alive ? 'Revenu sain et sauf' : 'Mort en expédition (permadeath)';

      const cardW = Math.min(540, GAME_W - 20);
      const lx = GAME_CX - cardW / 2 + 20;
      const rx = GAME_CX + cardW / 2 - 10;
      const rowH = _mobERS ? 46 : 32;
      const bg = this.add.rectangle(GAME_CX, y + rowH / 2, cardW, rowH, sr.alive ? 0x1a3a2a : 0x3a1a1a);
      bg.setStrokeStyle(1, sr.alive ? 0x2ecc71 : 0xe74c3c);
      this.panel.add(bg);

      this.panel.add(this.add.text(lx, y + 4, `${statusEmoji} ${sr.survivorName}`, {
        fontSize: '12px', color: statusColor, fontFamily: FONT_BODY, fontStyle: 'bold',
      }));

      // Sur mobile, le label passe sous le nom (sinon il chevauche dans 430 px)
      if (_mobERS) {
        this.panel.add(this.add.text(lx, y + 22, statusLabel, {
          fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
        }));
      } else {
        this.panel.add(this.add.text(rx, y + 5, statusLabel, {
          fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
        }).setOrigin(1, 0));
      }

      y += rowH;
      y += 6;
    }

    // === LEVEL-UPS (Sprint 85) — affichés uniquement si succès complet ===
    if (this.result.levelUps && this.result.levelUps.length > 0) {
      y += 10;
      this.panel.add(this.add.text(headerX, y, '--- ⭐ Niveaux gagnés ---', {
        fontSize: '11px', color: '#f1c40f', fontFamily: FONT_BODY,
      }));
      y += 22;
      for (const lu of this.result.levelUps) {
        const stars = '★'.repeat(lu.newLevel) + '☆'.repeat(5 - lu.newLevel);
        this.panel.add(this.add.text(itemX, y, `⭐ ${lu.survivorName} : Niv.${lu.oldLevel} → Niv.${lu.newLevel}  ${stars}`, {
          fontSize: '11px', color: '#f1c40f', fontFamily: FONT_BODY,
        }));
        y += 20;
      }
    }

    // === MESSAGE FINAL ===
    y += 15;
    if (this.result.deaths === 0) {
      this.panel.add(this.add.text(GAME_CX, y, '🎉 Expédition réussie sans pertes !', {
        fontSize: '13px', color: '#2ecc71', fontFamily: FONT_BODY,
      }).setOrigin(0.5));
    } else if (this.result.deaths < this.result.survivors.length) {
      this.panel.add(this.add.text(GAME_CX, y, '⚠️ L\'expédition a coûté des vies...', {
        fontSize: '13px', color: '#e67e22', fontFamily: FONT_BODY,
      }).setOrigin(0.5));
    } else {
      this.panel.add(this.add.text(GAME_CX, y, '💀 Aucun survivant de retour...', {
        fontSize: '13px', color: '#e74c3c', fontFamily: FONT_BODY,
      }).setOrigin(0.5));
    }

    y += 40;
    this.panel.setContentHeight(y);
  }

  /** Applique les récompenses au camp */
  private applyRewards(): void {
    const res = this.camp.getResources();

    // Ressources
    res.addMultiple(this.result.resources);

    // Or (ajouté au run en cours pour être visible)
    if (this.result.gold > 0) {
      this.run.addGold(this.result.gold);
    }

    // Item trouvé → coffre
    if (this.result.itemFound) {
      this.camp.addItemToStorage(this.result.itemFound);
    }

    // Morts → permadeath
    for (const sr of this.result.survivors) {
      if (!sr.alive) {
        this.camp.killSurvivor(sr.survivorId);
      }
    }

    // Sprint 85 — Level-ups effectifs pour les survivants vivants si zéro mort
    if (this.result.levelUps && this.result.levelUps.length > 0) {
      this.camp.levelUpSurvivors(this.result.levelUps.map(l => l.survivorId));
    }
  }

  shutdown(): void {
    this.panel?.destroy();
  }
}
